/**
 * Mode Camp / Hike de l'app Parents.
 *
 * Pendant un séjour, les familles des enfants inscrits — un enfant est inscrit
 * dès que son paiement lié au séjour est encaissé — ont un onglet dédié :
 * infos pratiques, planning révélé au jour le jour, photos rangées par jour et
 * publiées chaque soir par le staff.
 *
 * Les règles de calendrier vivent ici pour être les mêmes côté serveur (qui
 * décide de ce qu'il renvoie) et côté interface (qui l'annonce). Tous les
 * jours sont des jours calendaires à Bruxelles : un séjour se vit en heure
 * belge, quel que soit le fuseau du serveur.
 */

import type { Id, IsoDate, IsoDateTime } from './types.ts';

export const CAMP_TAB_DEFAULT_LEAD_DAYS = 7;
export const CAMP_TAB_DEFAULT_TAIL_DAYS = 30;
export const CAMP_TAB_MAX_LEAD_DAYS = 60;
export const CAMP_TAB_MAX_TAIL_DAYS = 365;
export const CAMP_JOURNAL_NOTE_MAX = 1500;
export const CAMP_REMINDER_DAYS_BEFORE = 3;

/**
 * Libellés des catégories livrées avec Bala-Camp. Les catégories se créent
 * désormais dans l'onglet Baygon (`camp_activity_categories`) : ces libellés ne
 * servent que de repli quand la table ne donne pas le sien.
 */
const DEFAULT_CAMP_CATEGORY_LABELS: Record<string, string> = {
  accueil: 'Accueil / Départ',
  grand_jeu: 'Grand jeu',
  veillee: 'Veillée',
  repas: 'Repas',
  routine: 'Vie de camp',
  hike: 'Hike',
  temps_libre: 'Temps libre',
};

export type CampType = 'camp' | 'hike';
export type CampRange = { startAt: IsoDateTime; endAt: IsoDateTime };
export type CampWindow = { leadDays: number; tailDays: number };

export interface ParentCampPlanningItem {
  startsAt: string;
  endsAt: string;
  title: string;
  /** Libellé de la catégorie du créneau (« Grand jeu », « Veillée »…), s'il est connu. */
  categoryLabel: string | null;
}

export interface ParentCampSummary {
  activityId: Id;
  type: CampType;
  title: string;
  startAt: IsoDateTime;
  endAt: IsoDateTime;
  /** Prénoms des enfants inscrits de la famille (vide en aperçu Baigon). */
  childNames: string[];
}

export interface ParentCampDay {
  day: IsoDate;
  dayIndex: number;
  /** Le planning de ce jour est-il déjà dévoilé (jour J ou passé) ? */
  revealed: boolean;
  theme: string | null;
  planning: ParentCampPlanningItem[];
  /** Photos et mot du jour publiés par le staff. */
  published: boolean;
  note: string | null;
  photos: Array<{ id: Id; fileId: Id; caption: string | null }>;
  coverFileId: Id | null;
}

export interface ParentCampPractical {
  meetingName: string | null;
  meetingAddress: string | null;
  startAt: IsoDateTime;
  endAt: IsoDateTime;
  documents: Array<{ id: Id; title: string; fileId: Id }>;
  material: string[];
  children: Array<{ id: Id; firstName: string; missingDocuments: string[] }>;
}

export interface ParentCampDetail extends ParentCampSummary {
  allowDownload: boolean;
  practical: ParentCampPractical;
  days: ParentCampDay[];
}

export interface CampJournalDay {
  activityId: Id;
  day: IsoDate;
  note: string | null;
  publishedAt: IsoDateTime | null;
  publishedBy: Id | null;
  updatedAt: IsoDateTime;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

const brusselsFormatter = new Intl.DateTimeFormat('fr-CA', {
  timeZone: 'Europe/Brussels',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Jour calendaire `YYYY-MM-DD` à Bruxelles. */
export function brusselsDay(at: Date | IsoDateTime): IsoDate {
  return brusselsFormatter.format(typeof at === 'string' ? new Date(at) : at);
}

/** Décale un jour calendaire, sans passer par un fuseau. */
function shiftDay(day: IsoDate, days: number): IsoDate {
  const [year, month, date] = day.split('-').map(Number);
  const shifted = new Date(Date.UTC(year!, month! - 1, date! + days));
  return shifted.toISOString().slice(0, 10);
}

/** Chaque jour du séjour, du départ au retour compris. */
export function campDayList(range: CampRange): IsoDate[] {
  const last = brusselsDay(range.endAt);
  const days: IsoDate[] = [];
  for (let day = brusselsDay(range.startAt); day <= last; day = shiftDay(day, 1)) {
    days.push(day);
    // Garde-fou : une date de fin aberrante ne doit pas produire une boucle
    // de plusieurs années.
    if (days.length > 366) break;
  }
  return days;
}

/** L'onglet est-il visible : de `leadDays` avant le départ à `tailDays` après le retour. */
export function isCampTabOpen(range: CampRange, window: CampWindow, now: Date): boolean {
  const today = brusselsDay(now);
  return (
    today >= shiftDay(brusselsDay(range.startAt), -window.leadDays)
    && today <= shiftDay(brusselsDay(range.endAt), window.tailDays)
  );
}

/** Séjour terminé : l'heure de retour est passée. */
export function isCampOver(range: CampRange, now: Date): boolean {
  return now.getTime() >= new Date(range.endAt).getTime();
}

/** Le planning d'un jour se découvre le jour même, pas avant. */
export function isPlanningDayRevealed(day: IsoDate, now: Date): boolean {
  return brusselsDay(now) >= day;
}

/**
 * Jour auquel ranger une photo : celui demandé, sinon aujourd'hui, ramené
 * dans les bornes du séjour (une photo triée le lendemain du retour va au
 * dernier jour).
 */
export function resolveJournalDay(requested: IsoDate | null | undefined, range: CampRange, now: Date): IsoDate {
  const first = brusselsDay(range.startAt);
  const last = brusselsDay(range.endAt);
  const candidate = requested && ISO_DAY.test(requested) ? requested : brusselsDay(now);
  if (candidate < first) return first;
  if (candidate > last) return last;
  return candidate;
}

/** Libellé de l'onglet : « Hike » seulement si tous les séjours visibles sont des hikes. */
export function campModeLabel(types: CampType[]): 'Camp' | 'Hike' {
  return types.length > 0 && types.every((type) => type === 'hike') ? 'Hike' : 'Camp';
}

/**
 * Séjour qui donne son nom à l'onglet : celui en cours, sinon le prochain
 * départ, sinon le dernier rentré.
 */
export function currentCamp<T extends CampRange>(camps: T[], now: Date): T | undefined {
  const time = now.getTime();
  const byStart = [...camps].sort((a, b) => Date.parse(a.startAt) - Date.parse(b.startAt));
  return byStart.find((camp) => Date.parse(camp.endAt) > time)
    ?? byStart.sort((a, b) => Date.parse(b.endAt) - Date.parse(a.endAt))[0];
}

/** « Hike » quand l'enfant part (ou est) en hike, « Camp » quand il part en camp. */
export function campTabLabel(camps: Array<CampRange & { type: CampType }>, now: Date): 'Camp' | 'Hike' {
  const camp = currentCamp(camps, now);
  return camp ? campModeLabel([camp.type]) : 'Camp';
}

/**
 * Ligne de planning Bala-Camp réduite à ce que voient les familles. Construite
 * champ par champ : l'histoire du jeu, les règles, le plan B, le matériel et
 * les responsables ne sortent jamais, même si la requête en ramenait. La
 * catégorie ne sort que par son libellé.
 */
export function toParentPlanningItem(row: Record<string, unknown>): ParentCampPlanningItem | null {
  const { starts_at: startsAt, ends_at: endsAt, title, category, category_label: label } = row;
  if (typeof startsAt !== 'string' || typeof endsAt !== 'string' || typeof title !== 'string') return null;
  const categoryLabel = typeof label === 'string' && label.trim()
    ? label.trim()
    : typeof category === 'string' ? (DEFAULT_CAMP_CATEGORY_LABELS[category] ?? null) : null;
  return { startsAt, endsAt, title, categoryLabel };
}

/**
 * Texte libre écrit par une personne (mot du jour) : sans
 * caractères de contrôle, sans rafale de lignes vides. `null` s'il ne reste
 * rien ou si c'est trop long — l'appelant refuse alors l'envoi.
 */
export function normalizeFreeText(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null;
  const cleaned = value
    .replace(/\r\n?/g, '\n')
    .replace(/[\u0000-\u0009\u000B-\u001F\u007F​-‏‪-‮⁦-⁩]/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  if (cleaned.length === 0 || cleaned.length > max) return null;
  return cleaned;
}

/** Documents administratifs qu'une famille doit encore rendre pour un enfant. */
export function missingAdminDocuments(child: {
  healthFormReturned: boolean;
  parentalAuthorizationReturned: boolean;
  registrationFormReturned: boolean;
  charterSigned: boolean;
}): string[] {
  const missing: string[] = [];
  if (!child.healthFormReturned) missing.push('Fiche santé');
  if (!child.parentalAuthorizationReturned) missing.push('Autorisation parentale');
  if (!child.registrationFormReturned) missing.push('Fiche d’inscription');
  if (!child.charterSigned) missing.push('Charte');
  return missing;
}

const campDayFormatter = new Intl.DateTimeFormat('fr-BE', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  timeZone: 'UTC',
});

/** « samedi 11 juillet » pour un jour `YYYY-MM-DD`, quel que soit le fuseau de l'appareil. */
export function formatCampDay(day: IsoDate): string {
  const [year, month, date] = day.split('-').map(Number);
  return campDayFormatter.format(new Date(Date.UTC(year!, month! - 1, date!)));
}
